import * as THREE from '@phdsoft/three';

/** Name of the culler-owned group that wraps a section's LightSection (Borders stay outside it). */
export const CULL_PROXY_NAME = '__cullProxy';

/** Name prefix (before the first '_') of the merged plate mesh of a light section. */
export const PLATE_MESH_PREFIX = 'SurfaceTypesMesh';

/** The parts of a `Section` the culler needs (structurally compatible with Section). */
export interface CullableSectionSource {
  group: THREE.Object3D;               // Section.group
  lightSectionGroup?: THREE.Object3D;  // Section.lightSectionGroup
  borderGroup?: THREE.Object3D;        // Section.borderGroup — never under the proxy; moved back out if an earlier wrap put it there
}

/**
 * One cullable light section. The culler decides per frame whether the whole
 * section (LightSection) is hidden and writes ONLY `proxy.visible`.
 */
export interface CullSection {
  source: CullableSectionSource;
  /**
   * '__cullProxy', parent of lightSectionGroup (not borderGroup). `null` means the
   * section cannot be hidden (not wrapped yet): it is never culled and never occludes.
   */
  proxy: THREE.Group | null;
  /** Meshes named SurfaceTypesMesh* under lightSectionGroup (occluders + occludees). */
  plates: THREE.Mesh[];
  /** All other meshes under lightSectionGroup (occludees only). */
  typeMeshes: THREE.Mesh[];
}

/**
 * True when the chain from `object` up ends at a THREE.Scene (null → false). With
 * `requireVisible`, also every node of the chain must be visible. A detached chain
 * (region reloaded/removed) is not rendered.
 */
export function isAttachedToScene(object: THREE.Object3D | null, requireVisible: boolean = false): boolean {
  let current = object;
  while (current) {
    if (requireVisible && !current.visible) {
      return false;
    }
    if ((current as THREE.Scene).isScene) {
      return true;
    }
    current = current.parent;
  }
  return false;
}

export function isPlateMesh(object: THREE.Object3D): boolean {
  return object instanceof THREE.Mesh && object.name?.split('_')[0] === PLATE_MESH_PREFIX;
}

/** Collect plates and type meshes of a source's lightSectionGroup into a CullSection. */
export function createCullSection(source: CullableSectionSource, proxy: THREE.Group | null): CullSection {
  const plates: THREE.Mesh[] = [];
  const typeMeshes: THREE.Mesh[] = [];
  source.lightSectionGroup?.traverse(obj => {
    if (!(obj instanceof THREE.Mesh)) {
      return;
    }
    if (isPlateMesh(obj)) {
      plates.push(obj);
    } else {
      typeMeshes.push(obj);
    }
  });
  return { source, proxy, plates, typeMeshes };
}

/**
 * Update `child`'s subtree world matrices, plus the nodes between `ancestor` (already
 * up to date) and `child` (the '__cullProxy' once the section is wrapped).
 */
function updateSubtreeBelow(ancestor: THREE.Object3D, child: THREE.Object3D | undefined): void {
  if (!child) {
    return;
  }
  const between: THREE.Object3D[] = [];
  let current = child.parent;
  while (current && current !== ancestor) {
    between.push(current);
    current = current.parent;
  }
  if (!current) {
    child.updateWorldMatrix(true, true); // not under `ancestor`: update its whole parent chain
    return;
  }
  for (let i = between.length - 1; i >= 0; i--) {
    between[i].updateWorldMatrix(false, false);
  }
  child.updateWorldMatrix(false, true);
}

const _groupInverse = new THREE.Matrix4();
const _relative = new THREE.Matrix4();
const _childBox = new THREE.Box3();

/**
 * Union of the geometry bounds of everything under lightSectionGroup
 * (Borders are not hidden by the culler, so they are not included), expressed in `source.group`-local space (so `group.matrixWorld`
 * turns it into an OBB). Includes currently hidden children: a larger box is
 * only more conservative. Returns an empty box when there is no geometry.
 */
export function computeSectionLocalBox(source: CullableSectionSource, target: THREE.Box3): THREE.Box3 {
  target.makeEmpty();
  // was: source.group.updateWorldMatrix(true, true); — updated the whole section subtree (hidden SurfaceElements/LinearElements too) on every rebuild
  source.group.updateWorldMatrix(true, false);
  updateSubtreeBelow(source.group, source.lightSectionGroup);
  // was: updateSubtreeBelow(source.group, source.borderGroup); — Borders are not hidden by the culler, so they do not bound what it hides
  _groupInverse.copy(source.group.matrixWorld).invert();

  const addObject = (obj: THREE.Object3D): void => {
    const geometry = (obj as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
    if (!geometry || !(geometry as THREE.BufferGeometry).isBufferGeometry || !geometry.getAttribute('position')) {
      return;
    }
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox();
    }
    if (!geometry.boundingBox || geometry.boundingBox.isEmpty()) {
      return;
    }
    _relative.multiplyMatrices(_groupInverse, obj.matrixWorld);
    _childBox.copy(geometry.boundingBox).applyMatrix4(_relative);
    target.union(_childBox);
  };

  source.lightSectionGroup?.traverse(addObject);
  // was: source.borderGroup?.traverse(addObject); — Borders are not hidden by the culler, so they do not bound what it hides
  return target;
}
